/**
 * Client for the verifieddit.com AI detection API.
 * Calls the verifieddit.com API directly (CORS enabled for drop.thephenom.app).
 * Falls back to backend proxy if direct call fails.
 */

import { generateThumbnail } from './generateThumbnail'

const VERIFIEDDIT_API = 'https://verifieddit.com/api'
const API_BASE = typeof window !== 'undefined' && window.location.hostname === 'www.thephenom.app'
  ? 'https://drop.thephenom.app' : ''
const BACKEND_API = `${API_BASE}/api/drop/verifieddit`

const THUMBNAIL_THRESHOLD = 7.5 * 1024 * 1024 // 7.5MB (10MB limit after base64)

export async function prepareImageForAiDetection(file: File): Promise<{
  imageData: string;
  mimeType: string;
  usedThumbnail: boolean;
}> {
  if (file.size > THUMBNAIL_THRESHOLD) {
    const thumbnailDataUri = await generateThumbnail(file)
    if (thumbnailDataUri) {
      const [, data] = thumbnailDataUri.split(',')
      return { imageData: data ?? '', mimeType: 'image/jpeg', usedThumbnail: true }
    }
    // generateThumbnail failed — fall through to raw base64 but still flag thumbnail attempted
    const buffer = await file.arrayBuffer()
    const bytes = new Uint8Array(buffer)
    let binary = ''
    const chunkSize = 8192
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
    }
    const imageData = btoa(binary)
    return { imageData, mimeType: file.type, usedThumbnail: true }
  }
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer)
    let binary = ''
    const chunkSize = 8192
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
    }
    const imageData = btoa(binary)
  return { imageData, mimeType: file.type, usedThumbnail: false }
}

export interface AiDetectApiResponse {
  aiDetected: boolean | null
  confidence: number | null
  detectedGenerator: string | null
  analysisDate: string
  fileHash: string
  cached: boolean
  error?: string
}

/**
 * Send an image to the server-side AI detection API via backend proxy.
 * Returns null on network error.
 */
export async function analyzeImageForAi(
  file: File,
  fileHash: string,
): Promise<AiDetectApiResponse | null> {
  try {
    const { imageData, mimeType } = await prepareImageForAiDetection(file)

    // Call verifieddit.com directly — supports anonymous auth (rate-limited by IP)
    // Falls back to backend proxy if direct call fails
    let res: Response
    try {
      res = await fetch(`${VERIFIEDDIT_API}/ai-detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          imageData,
          mimeType,
          fileHash,
          filename: file.name,
        }),
      })
    } catch {
      res = await fetch(`${BACKEND_API}/ai-detect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageData, mimeType, fileHash, filename: file.name }),
      })
    }

    if (res.status === 429) {
      const data = await res.json() as AiDetectApiResponse
      return { ...data, error: 'rate_limit' }
    }

    if (!res.ok) {
      console.warn(`[ai-detect] API returned ${res.status}`)
      return null
    }

    return await res.json() as AiDetectApiResponse
  } catch (err) {
    console.error('[ai-detect] API call failed:', err)
    return null
  }
}

/** Check if user has consented to server-side AI detection. */
export function hasAiDetectConsent(): boolean {
  try {
    return localStorage.getItem('phenom-ai-detect-consent') === 'true'
  } catch {
    return false
  }
}

/** Set user consent for server-side AI detection. */
export function setAiDetectConsent(consented: boolean): void {
  try {
    if (consented) {
      localStorage.setItem('phenom-ai-detect-consent', 'true')
    } else {
      localStorage.removeItem('phenom-ai-detect-consent')
    }
  } catch {
    // localStorage unavailable
  }
}
